import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Navigate, Outlet, useLocation, useNavigate, useParams} from "react-router-dom";
import {Box, CircularProgress, Stack} from "@mui/material";
import {toast} from "react-toastify";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import {SignInContext} from "../../context/SignInContext";
import {ProjectRoles} from "../../context/AppContext";
import {ProjectRepository} from "../../repositories/ProjectRepository";
import {setBaseURL} from "../../config/axios-chatbot";
import {setBaseMCPUrl} from "../../config/axios-mcp";
import {setKbManagerBaseURL} from "../../config/axios-kb-manager"
import {RegistryProjectLayout} from "../../layouts/registry-project-layout/RegistryProjectLayout";
import {RegistryProjectHeader} from "../../layouts/registry-project-layout/RegistryProjectHeader";
import {RegistryProjectContext, useRegistryProject} from "./RegistryProjectContext";
import {registryPages} from "./registry-project-routes";
import {RegistryPagePlaceholder} from "./RegistryPagePlaceholder";

export const projectPath = (projectId, subProjectId, subPath = "") =>
  `/registry-project/${projectId}/${subProjectId}${subPath ? `/${subPath}` : ""}`;

// /registry-project/:registryProjectId - loads the project and its members once for every page below
export const RegistryProjectRoute = () => {
  const {registryProjectId} = useParams();
  const {currentUser} = useContext(SignInContext);
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  // useNavigate returns a new function on every location change (e.g. switching language); keeping it out of
  // the dependencies stops a language switch from reloading the whole project
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const lastGoodRef = useRef(null);
  if (project) lastGoodRef.current = {project, members};

  if (project && String(project.id) !== String(registryProjectId)) {
    setProject(null);
  }

  const refreshProject = useCallback(async () => {
    try {
      const [projectResponse, membersResponse] = await Promise.all([
        ProjectRepository.getById(registryProjectId),
        ProjectRepository.getMembers(registryProjectId),
      ]);
      if (!projectResponse.data?.subProjects?.length) {
        toast.error("This project has no sub projects");
        navigateRef.current("/projects", {replace: true});
        return;
      }
      // the project's pages call the services of its backend configuration; set before the pages render
      const backendConfiguration = projectResponse.data.backendConfiguration;
      console.log(projectResponse?.data);
      setBaseURL(backendConfiguration?.conversationLoggerApi || process.env.REACT_APP_CHATBOT_BACKEND, registryProjectId);
      setBaseMCPUrl(backendConfiguration?.mcpRegistry || "");
      setKbManagerBaseURL(backendConfiguration?.knowledgeManagerApi || "");
      setProject(projectResponse.data);
      setMembers(membersResponse.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to load project");
      navigateRef.current("/projects", {replace: true});
    }
  }, [registryProjectId]);

  // full reload only when opening a different project
  useEffect(() => {
    refreshProject();
  }, [refreshProject]);

  const role = useMemo(() => {
    if (currentUser?.roles?.includes("admin")) return ProjectRoles.ADMIN;
    return members.find(member => member?.username === currentUser?.username)?.role || null;
  }, [currentUser, members]);

  const isAnnotator = useMemo(
    () => members.find(member => member?.username === currentUser?.username)?.role === ProjectRoles.ANNOTATOR,
    [currentUser, members]
  );

  if (!project) {
    const previous = lastGoodRef.current;
    if (previous) {
      const previousRole = currentUser?.roles?.includes("admin")
        ? ProjectRoles.ADMIN
        : previous.members.find(member => member?.username === currentUser?.username)?.role || null;
      return (
        <RegistryProjectContext.Provider value={{
          project: previous.project, members: previous.members, role: previousRole,
          refreshProject, subProject: null, basePath: "",
        }}>
          <Stack direction={"column"} sx={{width: "100vw", height: "100vh", overflow: "hidden", bgcolor: "transparent"}}>
            <RegistryProjectHeader project={previous.project} role={previousRole}/>
            <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#F7F7FB"}}>
              <CircularProgress size={28} sx={{color: "#8491F7"}}/>
            </Box>
          </Stack>
        </RegistryProjectContext.Provider>
      );
    }
    return <LoadingScreen/>;
  }

  if (!role) {
    return <Navigate to={"/projects"} replace/>;
  }

  return (
    <RegistryProjectContext.Provider value={{project, members, role, isAnnotator, refreshProject, subProject: null, basePath: ""}}>
      <Outlet/>
    </RegistryProjectContext.Provider>
  );
};

// /registry-project/:registryProjectId (no sub project) - open the first sub project
export const RegistryDefaultSubProjectRedirect = () => {
  const {project} = useRegistryProject();
  return <Navigate to={projectPath(project.id, project.subProjects[0].id)} replace/>;
};

// /registry-project/:registryProjectId/:subProjectId - selects the sub project and renders the layout.
// Links from before sub projects used the language code here; those open the first sub project of that
// language. Anything else unknown falls back to the first sub project, keeping the current page.
export const RegistrySubProjectRoute = () => {
  const {subProjectId} = useParams();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const context = useRegistryProject();
  const {project, role} = context;

  const prefix = projectPath(project.id, subProjectId);
  const subPath = pathname.startsWith(prefix) ? pathname.slice(prefix.length).replace(/^\//, "") : "";
  const subProject = project.subProjects.find(item => String(item.id) === subProjectId);

  if (!subProject) {
    const fallback = project.subProjects.find(item => item.languageCode === subProjectId) || project.subProjects[0];
    return <Navigate to={projectPath(project.id, fallback.id, subPath)} replace/>;
  }

  const basePath = projectPath(project.id, subProject.id);
  // a designer flow id belongs to this sub project's flow document, so another sub project opens its first flow
  const changeSubProject = (id) => navigate(projectPath(project.id, id, subPath.replace(/^flow\/.*/, "flow")));

  return (
    <RegistryProjectContext.Provider value={{...context, subProject, basePath, changeSubProject}}>
      <RegistryProjectLayout
        project={project}
        subProject={subProject}
        role={role}
        basePath={basePath}
      >
        <Outlet/>
      </RegistryProjectLayout>
    </RegistryProjectContext.Provider>
  );
};

const landingPath = (role, basePath) =>
  role === ProjectRoles.ANNOTATOR ? `${basePath}/supervision` : basePath;

// One menu page: checks the project role, then renders the rebuilt page or a placeholder
export const RegistryPageRoute = ({label, path, roles}) => {
  const {role, subProject, basePath} = useRegistryProject();

  if (!roles.includes(role)) {
    return <Navigate to={landingPath(role, basePath)} replace/>;
  }

  const Page = registryPages[path];
  return Page
    ? <Page/>
    : <RegistryPagePlaceholder label={label} path={path} subProject={subProject}/>;
};

// Unknown page under a sub project - back to the role's landing page (the dashboard, Supervision for annotators)
export const RegistryNotFoundRedirect = () => {
  const {role, basePath} = useRegistryProject();
  return <Navigate to={landingPath(role, basePath)} replace/>;
};
